import React from 'react'
import { Layout, Menu, Avatar, Dropdown, Space, Typography } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import { 
  HomeOutlined, 
  LineChartOutlined, 
  DollarOutlined, 
  UserOutlined,
  LogoutOutlined,
  SettingOutlined
} from '@ant-design/icons'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { logout } from '../../store/slices/authSlice'
import { useLanguage } from '../../contexts/LanguageContext'
import BitnukLogo from '../Common/BitnukLogo'
import { DEFAULT_TRADE_DISPLAY_SYMBOL } from '../../config/futuresDisplay'

const { Header: AntHeader } = Layout
const { Text } = Typography

const Header: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { t } = useLanguage()
  const { user } = useAppSelector((state) => state.auth)

  const tradePath = `/trade/${DEFAULT_TRADE_DISPLAY_SYMBOL}`

  const menuItems = [
    {
      key: '/market',
      icon: <HomeOutlined />,
      label: t('navigation.market'),
    },
    {
      key: tradePath,
      icon: <LineChartOutlined />,
      label: t('navigation.trade'),
    },
    {
      key: '/mine',
      icon: <UserOutlined />,
      label: t('navigation.mine'),
    },
  ]

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('profile.profile'),
      onClick: () => navigate('/mine'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('profile.securityCenter'),
      onClick: () => navigate('/security-center'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('profile.logout'),
      onClick: () => {
        dispatch(logout())
        navigate('/login')
      },
    },
  ]

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key)
  }

  return (
    <AntHeader className="bg-dark-surface border-b border-dark-border px-6 flex items-center justify-between">
      {/* Logo */}
      <div className="flex items-center">
        <div
          className="cursor-pointer hover:opacity-90 transition-opacity mr-8"
          onClick={() => navigate('/home')}
        >
          <BitnukLogo height={28} />
        </div>
      </div>

      {/* 主导航菜单 */}
      <div className="flex items-center space-x-8 flex-1 justify-center">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.key || 
            (item.key === tradePath && (location.pathname.startsWith('/trade') || location.pathname.startsWith('/spot-trading') || location.pathname.startsWith('/margin-trading') || location.pathname.startsWith('/seconds-trading')))
          
          return (
            <button
              key={item.key}
              onClick={() => navigate(item.key)}
              className={`
                flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-[#f0b90b] text-black font-medium shadow-lg' 
                  : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                }
              `}
            >
              <span className={`text-lg ${isActive ? 'text-black' : ''}`}>
                {item.icon}
              </span>
              <span className="hidden md:inline font-medium">
                {item.label}
              </span>
            </button>
          )
        })}
      </div>

      {/* 用户信息 */}
      <Space>
        <Dropdown
          menu={{ items: userMenuItems }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Space className="cursor-pointer hover:bg-dark-border rounded px-3 py-2 transition-colors">
            <Avatar 
              src={user?.avatar} 
              icon={<UserOutlined />}
              size="small"
            />
            <Text className="text-white hidden md:inline">
              {user?.name}
            </Text>
          </Space>
        </Dropdown>
      </Space>
    </AntHeader>
  )
}

export default Header